import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowUpRight } from 'lucide-react';
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';

export default function ListingCard({ listing }) {
  const { user } = useAuth();
  const isMember = user?.role === 'member' || user?.role === 'admin';

  const imageUrl = listing.images?.length > 0 ? getImageUrl(listing.images[0]) : PLACEHOLDER_IMAGE;

  const formatPrice = (price, currency) => {
    if (!price) return 'POA';
    const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
    return `${sym}${Number(price).toLocaleString()}`;
  };

  return (
    <Link to={`/listings/${listing.id}`} className="group block" data-testid="listing-card">
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: '360px', backgroundColor: '#F5F5F5' }}>
        <img
          src={imageUrl}
          alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105"
          onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
        />
        {listing.featured && (
          <span className="absolute top-4 left-4 text-[10px] px-3 py-1.5 uppercase tracking-[0.15em] font-semibold" style={{ backgroundColor: '#000', color: '#fff' }}>
            Featured
          </span>
        )}
        {listing.status === 'sold' && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(10,10,10,0.55)' }}>
            <span className="text-white font-bold text-xl uppercase tracking-widest">Sold</span>
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-end p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.55), rgba(10,10,10,0) 55%)' }}>
          <span className="inline-flex items-center gap-2 text-white text-xs font-semibold uppercase tracking-[0.15em]">
            View Work <ArrowUpRight size={15} />
          </span>
        </div>
      </div>

      {/* Meta */}
      <div className="pt-4">
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: '#999' }}>
            {listing.category_name || 'Artwork'}
          </p>
          {isMember ? (
            <span className="text-[11px]" style={{ color: '#999' }}>{listing.seller?.name || 'Private seller'}</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#999' }}>
              <Lock size={10} /> Members only
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-4 pt-3" style={{ borderTop: '1px solid #E5E5E5' }}>
          <div className="min-w-0">
            <h3 className="text-base font-bold leading-snug line-clamp-1" style={{ color: '#000' }} data-testid="listing-card-title">
              {listing.title}
            </h3>
            {listing.medium && <p className="text-xs mt-0.5" style={{ color: '#666' }}>{listing.medium}</p>}
          </div>
          <p className="text-base font-bold whitespace-nowrap" style={{ color: '#000' }}>
            {formatPrice(listing.price, listing.currency)}
          </p>
        </div>
      </div>
    </Link>
  );
}
